import ExcelJS from 'exceljs';

// Report views receive their tables as `response.meta.tables` — an array of
// single-key objects wrapping { title, tableHeaders, tableData }. The headers
// declare which row fields are display columns; rows also carry internal fields
// (notably `href` for row-click navigation) that must never reach an export.

const MAX_SHEET_NAME_LENGTH = 31;
const FORBIDDEN_SHEET_NAME_CHARS = /[:\\/?*[\]]/g;

function unwrapTable(tableObj, index) {
    if (!tableObj || typeof tableObj !== 'object') {
        throw new Error(`Report export: table at index ${index} is not an object`);
    }
    const table = Object.values(tableObj)[0];
    if (!table) {
        throw new Error(`Report export: table at index ${index} is empty`);
    }
    if (!table.title) {
        throw new Error(`Report export: table at index ${index} is missing a title`);
    }
    if (!Array.isArray(table.tableHeaders)) {
        throw new Error(`Report export: table "${table.title}" is missing tableHeaders`);
    }
    if (!Array.isArray(table.tableData)) {
        throw new Error(`Report export: table "${table.title}" is missing tableData`);
    }
    return table;
}

function sheetName(title, taken) {
    const base = title
        .replace(FORBIDDEN_SHEET_NAME_CHARS, '')
        .trim()
        .slice(0, MAX_SHEET_NAME_LENGTH);

    if (!taken.has(base)) {
        taken.add(base);
        return base;
    }

    for (let n = 2; ; n += 1) {
        const suffix = ` (${n})`;
        const name = base.slice(0, MAX_SHEET_NAME_LENGTH - suffix.length) + suffix;
        if (!taken.has(name)) {
            taken.add(name);
            return name;
        }
    }
}

// Projects the API's table structure into a flat, renderer-agnostic shape:
// [{ name, headers: [label], rows: [[cell]] }]. Both the CSV and XLSX writers
// consume this, so column selection and ordering are decided in exactly one place.
function buildSheets(tables) {
    if (!Array.isArray(tables)) {
        throw new Error('Report export: tables must be an array');
    }

    const taken = new Set();

    return tables.map((tableObj, index) => {
        const table = unwrapTable(tableObj, index);

        table.tableHeaders.forEach((header, headerIndex) => {
            if (!header?.key) {
                throw new Error(
                    `Report export: header at index ${headerIndex} in table "${table.title}" has no key`,
                );
            }
        });

        const keys = table.tableHeaders.map((header) => header.key);

        return {
            name: sheetName(table.title, taken),
            headers: table.tableHeaders.map((header) => header.title ?? header.key),
            // An absent cell is legitimately absent data, not a structural fault —
            // render it blank rather than throwing.
            rows: table.tableData.map((row) => keys.map((key) => row?.[key] ?? '')),
        };
    });
}

function csvCell(value) {
    const text = String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function toCsv(sheet) {
    const lines = [sheet.headers, ...sheet.rows].map((row) => row.map(csvCell).join(','));
    return lines.join('\r\n');
}

async function buildXlsxBuffer(sheets) {
    if (!Array.isArray(sheets) || sheets.length === 0) {
        throw new Error('Report export: at least one sheet is required');
    }

    const workbook = new ExcelJS.Workbook();

    sheets.forEach((sheet) => {
        const worksheet = workbook.addWorksheet(sheet.name);
        worksheet.addRow(sheet.headers);
        // Raw values, so numbers stay numeric and Excel can total the column.
        sheet.rows.forEach((row) => worksheet.addRow(row));
        worksheet.getRow(1).font = { bold: true };
    });

    return workbook.xlsx.writeBuffer();
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function useReportExport() {
    const exportXlsx = async (tables, filename) => {
        const buffer = await buildXlsxBuffer(buildSheets(tables));
        downloadBlob(
            new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            `${filename}.xlsx`,
        );
    };

    const exportCsv = (tables, filename) => {
        const sheets = buildSheets(tables);
        if (sheets.length === 0) {
            throw new Error('Report export: at least one sheet is required');
        }
        // CSV has no concept of multiple sheets; separate each with its title.
        const csv = sheets.length === 1
            ? toCsv(sheets[0])
            : sheets.map((sheet) => `${sheet.name}\r\n${toCsv(sheet)}`).join('\r\n\r\n');
        downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${filename}.csv`);
    };

    return { buildSheets, toCsv, buildXlsxBuffer, exportXlsx, exportCsv };
}

export {
    useReportExport,
};
